import { useEffect, useRef, useState } from 'react';
import { Command } from 'cmdk';
import { Check, ChevronsUpDown, Loader2, Search } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

export interface ComboboxOption {
  value: string;
  label: string;
  hint?: string;
  disabled?: boolean;
}

interface ComboboxProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: ComboboxOption[];
  placeholder?: string;
  emptyMessage?: string;
  emptyAction?: React.ReactNode;
  disabled?: boolean;
  loading?: boolean;
  required?: boolean;
  error?: string;
  hint?: string;
  fullWidth?: boolean;
  /** Total count to show when filtering ("12 résultats trouvés"). */
  resultCount?: number;
}

export function Combobox({
  label,
  value,
  onChange,
  options,
  placeholder = 'Rechercher…',
  emptyMessage = 'Aucun résultat',
  emptyAction,
  disabled,
  loading,
  required,
  error,
  hint,
  fullWidth,
  resultCount,
}: ComboboxProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  const selected = options.find((o) => o.value === value);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent): void => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const inputId = `cb-${label.replace(/\s+/g, '-').toLowerCase()}`;

  return (
    <div className={cn('relative space-y-1.5', fullWidth && 'sm:col-span-2')} ref={ref}>
      <Label htmlFor={inputId}>
        {label}
        {required && <span className="text-destructive"> *</span>}
      </Label>
      <button
        id={inputId}
        type="button"
        disabled={disabled}
        aria-invalid={!!error}
        onClick={() => setOpen((o) => !o)}
        className={cn(
          'flex h-9 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
          'disabled:cursor-not-allowed disabled:opacity-50',
          !selected && 'text-muted-foreground',
        )}
      >
        <span className="truncate text-left">
          {loading ? (
            <span className="inline-flex items-center gap-1 text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" /> Chargement…
            </span>
          ) : selected ? (
            selected.label
          ) : (
            placeholder
          )}
        </span>
        <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 opacity-50" />
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-full rounded-md border border-border bg-popover text-popover-foreground shadow-lg">
          <Command shouldFilter className="overflow-hidden rounded-md">
            <div className="flex items-center border-b border-border px-3">
              <Search className="mr-2 h-3.5 w-3.5 shrink-0 opacity-50" />
              <Command.Input
                value={search}
                onValueChange={setSearch}
                placeholder={placeholder}
                className="flex h-9 w-full bg-transparent py-1 text-sm outline-none placeholder:text-muted-foreground"
              />
              {resultCount !== undefined && (
                <span className="ml-2 shrink-0 text-[10px] text-muted-foreground">
                  {resultCount} résultat{resultCount > 1 ? 's' : ''}
                </span>
              )}
            </div>
            <Command.List className="scrollbar-thin max-h-64 overflow-y-auto p-1">
              <Command.Empty className="px-3 py-3 text-center text-xs text-muted-foreground">
                <div>{emptyMessage}</div>
                {emptyAction && <div className="mt-2">{emptyAction}</div>}
              </Command.Empty>
              {options.map((opt) => (
                <Command.Item
                  key={opt.value}
                  value={`${opt.label} ${opt.hint ?? ''} ${opt.value}`}
                  disabled={opt.disabled}
                  onSelect={() => {
                    if (opt.disabled) return;
                    onChange(opt.value);
                    setOpen(false);
                    setSearch('');
                  }}
                  className={cn(
                    'flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm',
                    'aria-selected:bg-accent aria-selected:text-accent-foreground',
                    opt.disabled && 'cursor-not-allowed opacity-50',
                  )}
                >
                  <Check
                    className={cn('h-3.5 w-3.5', opt.value === value ? 'opacity-100' : 'opacity-0')}
                  />
                  <div className="flex flex-1 items-center justify-between gap-2">
                    <span className="truncate">{opt.label}</span>
                    {opt.hint && (
                      <span className="shrink-0 text-[10px] text-muted-foreground">{opt.hint}</span>
                    )}
                  </div>
                </Command.Item>
              ))}
            </Command.List>
          </Command>
        </div>
      )}
      {error && <p className="text-xs text-destructive">{error}</p>}
      {!error && hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}
